import { addCopyButton, addLoadAccountDetailsButton, encodeEntities } from './main';
import { removeSpinners } from './sidebar';
import { accountIdPattern } from './accounts';
import DataTableLib from 'datatables.net-dt';

/**
 * Shorthand factory for a standard DataTable column definition.
 * Replaces the verbose 10-line object literal used across all table files.
 */
export function col(data, name, title, opts = {}) {
  const def = {
    visible: opts.visible ?? true,
    defaultContent: opts.defaultContent ?? '-',
    orderable: opts.orderable ?? true,
    searchable: opts.searchable ?? true,
    type: opts.type ?? 'string',
    data,
    name,
    title,
    createdCell: opts.createdCell ?? addCopyButton,
  };
  if (opts.class) def.class = opts.class;
  if (opts.width) def.width = opts.width;
  if (opts.className) def.className = opts.className;
  if (opts.render) def.render = opts.render;
  return def;
}

/**
 * The detail-expand (chevron) column used as the first column in most tables.
 */
export function selectColumn(opts = {}) {
  return {
    visible: true,
    defaultContent: '',
    orderable: false,
    searchable: false,
    data: null,
    name: opts.name ?? 'select_col',
    class: 'details-control',
    ...(opts.width && { width: opts.width }),
  };
}

const regionPattern = /^[a-z]{2}-[a-z]{1,12}-\d$/;

export function accountIdCreatedCell(td, cellData) {
  addCopyButton(td);
  addLoadAccountDetailsButton(td);
  if (!accountIdPattern.test(cellData)) {
    $(td).addClass('portal-danger');
  }
}

export function regionCreatedCell(td, cellData) {
  addCopyButton(td);
  if (!regionPattern.test(cellData)) {
    $(td).addClass('portal-danger');
  }
}

export function executionStatusCreatedCell(td, cellData) {
  addCopyButton(td);
  switch (cellData) {
    case 'SUCCESS':
    case 'SUCCESS_NO_ACTIONS':
      $(td).addClass('portal-success');
      break;
    case 'FAILED':
    case 'NOT_COMPLETED':
      $(td).addClass('portal-danger');
      break;
    case 'IN_PROGRESS':
      $(td).addClass('portal-warning');
      break;
    default:
      $(td).addClass('portal-neutral');
      break;
  }
}

// Debounced resize handler — prevents layout thrashing when DataTables
// fires many rapid draw events (e.g. during paginated data loading).
let _resizeTimer = null;
function debouncedResize() {
  if (_resizeTimer) return;
  _resizeTimer = setTimeout(() => {
    _resizeTimer = null;
    $(window).trigger('resize');
  }, 100);
}

// Leading chars that spreadsheet apps (Excel/Sheets) treat as formula triggers,
// including full-width variants recognised in some locales (e.g. Japanese).
const FORMULA_INJECTION_PATTERN = /^[=+\-@\t\r\n＝＋－＠]/;

/**
 * Neutralises CSV/Excel formula injection by prefixing risky cell values with
 * a tab character
 */
export function sanitizeExportCell(data) {
  if (typeof data !== 'string') return data;
  return FORMULA_INJECTION_PATTERN.test(data) ? `\t${data}` : data;
}

const EXPORT_BUTTON_EXTENDS = new Set(['csvHtml5', 'excelHtml5', 'copyHtml5']);

// Applied centrally in initDataTable so every table gets formula-injection
// sanitization by default, without each page having to opt in.
function withSanitizedExports(buttons) {
  return (buttons || []).map(button => {
    if (!EXPORT_BUTTON_EXTENDS.has(button.extend)) return button;
    return {
      ...button,
      exportOptions: {
        ...button.exportOptions,
        format: { ...button.exportOptions?.format, body: sanitizeExportCell },
      },
    };
  });
}

export function initDataTable(tableId, buttons, columns, rowCallback, additionalAttributes) {
  const sanitizedButtons = withSanitizedExports(buttons);
  const layout = {
    topStart: 'pageLength',
    topEnd: ['search', 'colvisCustom'],
    bottomStart: {
      buttons: sanitizedButtons,
    },
    bottomEnd: {
      paging: {
        firstLast: false,
      },
    },
    bottom2Start: 'info',
  };

  const language = {
    processing:
      '<span class="spinner-border-container"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></span>',
    lengthMenu: 'Show _MENU_ entries',
    paginate: {
      next: 'Next',
      previous: 'Previous',
    },
  };

  const lengthMenu = [10, 25, 50, { label: 'All', value: -1 }];

  let dt_config = {
    autoWidth: false,
    retrieve: true,
    scrollCollapse: true,
    scrollX: true,
    processing: true,
    deferRender: true,
    language: language,
    lengthMenu: lengthMenu,
    layout: layout,
    stateSave: true,
    buttons: sanitizedButtons,
    columns: columns,
    rowCallback: rowCallback,
    columnDefs: [
      {
        targets: '_all',
        render: {
          display: encodeEntities,
        },
      },
    ],
    initComplete: function () {
      $(window).trigger('resize');
    },
    drawCallback: function () {
      debouncedResize();
      removeSpinners();
    },
  };

  if (rowCallback) {
    dt_config.rowCallback = rowCallback;
  }

  if (additionalAttributes) {
    dt_config = Object.assign(dt_config, additionalAttributes);
  }

  return new DataTableLib(`#${tableId}`, dt_config);
}

/**
 * Standard export buttons used in most DataTable configurations.
 * @param {{ excel?: boolean, csv?: boolean, copy?: boolean }} options
 */
export function exportButtons({ excel = true, csv = true, copy = true } = {}) {
  const buttons = [];
  if (excel)
    buttons.push({
      extend: 'excelHtml5',
      text: 'Export Excel',
      exportOptions: { columns: ':visible' },
      titleAttr: 'Export the visible columns as Excel file',
    });
  if (csv)
    buttons.push({
      extend: 'csvHtml5',
      text: 'Export CSV',
      exportOptions: { columns: ':visible' },
      titleAttr: 'Export the visible columns as CSV file',
    });
  if (copy)
    buttons.push({
      extend: 'copyHtml5',
      text: 'Copy',
      exportOptions: { columns: ':visible' },
      titleAttr: 'Copy the visible columns into your clipboard',
    });
  return buttons;
}

export function resetTableButton() {
  return { extend: 'resetTable', ajaxReload: false, titleAttr: 'Reset all filters in the table footer' };
}

export function reloadTableButton(text, methodReload, titleAttr) {
  return { extend: 'reloadTable', text, ajaxReload: false, methodReload, titleAttr: titleAttr || text };
}

export function updateTableFooter(tableId, tableColumns, dropdownColumns, searchColumns, searchColumnsNumbers) {
  const dt = $('#' + tableId).dataTable();
  if (!dt.api().state()) return;
  const columnsState = dt.api().state().columns;

  dt.api()
    .columns()
    .every(function (col) {
      const that = this;
      const column = this;
      const title = column.header().textContent;
      const dataSource = column.dataSrc();
      const id = tableColumns[column.index()]?.id;

      if (dropdownColumns.indexOf(dataSource) > -1) {
        const select = $('<select><option value=""></option></select>')
          .appendTo($(column.footer()).empty())
          .on('change', function () {
            const val = $.fn.dataTable.util.escapeRegex($(this).val());
            column.search(val ? '^' + val + '$' : '', true, false).draw();
          });

        column
          .data()
          .unique()
          .sort()
          .each(function (data) {
            let opt = document.createElement('option');
            let newData = data || '-';
            opt.innerText = newData;
            opt.value = newData;
            select.append(opt);

            if (
              data &&
              columnsState[col].search &&
              columnsState[col].search.search &&
              data.match(columnsState[col].search.search)
            ) {
              opt.selected = true;
            }
          });
      }

      if (searchColumns.indexOf(dataSource) > -1 || searchColumns.indexOf(id) > -1) {
        $(column.footer()).html('<input type="text" placeholder="Search ' + title + '" />');

        if (columnsState[col].search && columnsState[col].search.search) {
          column.footer().lastChild.value = columnsState[col].search.search;
        }

        $('input', that.footer()).on('keyup change', function () {
          if (that.search() !== this.value) {
            that.search(this.value).draw();
          }
        });
      }

      if (searchColumnsNumbers.indexOf(dataSource) > -1 || searchColumnsNumbers.indexOf(id) > -1) {
        $(column.footer()).html('<input type="text" placeholder="Search ' + title + '" />');
        $('input', that.footer()).on('keyup change', function () {
          if (this.value) {
            if (that.search() !== this.value) {
              that.search('^' + this.value + '$', true, false).draw();
            }
          } else {
            that.search('').draw();
          }
        });
      }
    });
}
